import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import Icon from "@mdi/react";
import { mdiArrowLeft, mdiDownload } from "@mdi/js";
import {
  Button,
  Chip,
  ListBox,
  SearchField,
  Select,
  Spinner,
  Table,
  Tooltip,
  Modal,
} from "@heroui/react";
import { ROUTES } from "@/constants/routes";
import { pushCampaignsStore } from "@/stores/PushCampaignsStore";
import type { PushCampaignDetail } from "@/stores/PushCampaignsStore";
import {
  BUSINESS_TYPE_LABELS,
  CAMPAIGN_STATUS_LABELS,
  FUNNEL_STAGES,
  FUNNEL_STAGE_LABELS,
  isPushCampaignEditable,
  LINK_KIND_LABELS,
  RECIPIENT_STATUS_LABELS,
  SKIP_REASON_LABELS,
} from "@/stores/push-campaigns/pushCampaignLabels";
import "./PushCampaignDetailPage.css";

const STATUS_CHIP_COLOR: Record<
  string,
  "default" | "success" | "warning" | "danger" | "accent"
> = {
  draft: "default",
  scheduled: "warning",
  sending: "accent",
  sent: "success",
  cancelled: "default",
  failed: "danger",
};

function formatCreatedShort(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("ru-RU", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function buildMetaLine(c: PushCampaignDetail): string {
  const parts: string[] = [];
  if (c.authorName) parts.push(c.authorName);
  const created = formatCreatedShort(c.createdAt);
  if (created) parts.push(`создана ${created}`);
  if (c.targetBusinessType) {
    parts.push(
      BUSINESS_TYPE_LABELS[c.targetBusinessType] ?? c.targetBusinessType,
    );
  } else {
    parts.push("Любой тип бизнеса");
  }
  if (c.linkKind) {
    parts.push(LINK_KIND_LABELS[c.linkKind] ?? c.linkKind);
  }
  if (c.linkKind === "service_center" && c.linkServiceCenterName) {
    parts.push(c.linkServiceCenterName);
  }
  return parts.join(" · ");
}

function pct(part: number, total: number): string {
  if (!total) return "—";
  return `${Math.round((part / total) * 100)}%`;
}

function formatRevenue(rub: number | null | undefined): string {
  if (rub == null) return "—";
  return rub.toLocaleString("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  });
}

const PushCampaignDetailPage = observer(() => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const store = pushCampaignsStore;
  const [cancelOpen, setCancelOpen] = useState(false);
  const c = store.detail;

  useEffect(() => {
    if (!uuid) return;
    let active = true;
    void (async () => {
      await store.loadCampaignDetail(uuid);
      if (!active) return;
      await store.loadRecipients(uuid);
    })();
    return () => {
      active = false;
      store.disposeDetail();
    };
  }, [uuid, store]);

  if (!uuid) return null;

  const canMutate = isPushCampaignEditable(c?.status);
  const delivered = c?.deliveredCount ?? 0;
  const sent = c?.sentCount ?? c?.recipientsTotal ?? 0;
  const opened = c?.openedCount ?? 0;
  const ctrBase = delivered > 0 ? delivered : sent;
  const ctrLabel =
    ctrBase > 0 ? `CTR ${pct(opened, ctrBase)}` : "CTR —";
  const funnelHint =
    delivered > 0
      ? ctrLabel
      : `${ctrLabel} · Квитанций ещё нет: открыли / отправлено`;

  const funnelCounts: number[] = FUNNEL_STAGES.map(
    (stage) => c?.funnel?.[stage] ?? 0,
  );

  const recipientsTotal = store.recipientsMeta?.total ?? 0;
  const recipientsShown = store.recipients.length;

  return (
    <>
      <div className="pcd-page" data-testid="push-campaign-detail">
        <header className="pcd-header">
          <div className="pcd-header__main">
            <div className="pcd-header__title-row">
              <button
                type="button"
                className="pcd-back"
                aria-label="К списку рассылок"
                onClick={() => navigate(ROUTES.PUSH_CAMPAIGNS)}
              >
                <Icon path={mdiArrowLeft} size={0.85} />
              </button>
              <h1 className="pcd-header__title">{c?.name ?? "…"}</h1>
              {c?.status && (
                <Chip
                  size="sm"
                  variant="soft"
                  color={STATUS_CHIP_COLOR[c.status] ?? "default"}
                >
                  {CAMPAIGN_STATUS_LABELS[c.status] ?? c.status}
                </Chip>
              )}
            </div>
            {c && <p className="pcd-header__meta">{buildMetaLine(c)}</p>}
            {c?.status === "scheduled" && !c.recipientsTotal && (
              <p className="text-sm text-amber-700 mt-1">
                Снимок аудитории будет собран в момент отправки.
              </p>
            )}
          </div>
          <div className="pcd-header__actions">
            <Button
              variant="outline"
              onPress={() =>
                navigate(ROUTES.PUSH_CAMPAIGN_EDIT.replace(":uuid", uuid))
              }
            >
              Редактировать
            </Button>
            {canMutate && (
              <Button variant="outline" onPress={() => setCancelOpen(true)}>
                Отменить
              </Button>
            )}
            <Button
              variant="outline"
              onPress={async () => {
                const newId = await store.duplicateCampaign(uuid);
                if (newId) {
                  navigate(
                    ROUTES.PUSH_CAMPAIGN_EDIT.replace(":uuid", newId),
                  );
                }
              }}
            >
              Дублировать
            </Button>
          </div>
        </header>

        <div className="pcd-metrics">
          <div className="pcd-metric">
            <div className="pcd-metric__label">Получатели</div>
            <div className="pcd-metric__value">{c?.recipientsTotal ?? "—"}</div>
          </div>
          <div className="pcd-metric">
            <div className="pcd-metric__label">Пропущены</div>
            <div className="pcd-metric__value">{c?.skippedCount ?? "—"}</div>
          </div>
          <div className="pcd-metric">
            <div className="pcd-metric__label">Отправлено</div>
            <div className="pcd-metric__value">{sent || "—"}</div>
          </div>
          <div className="pcd-metric">
            <div className="pcd-metric__label">
              <Tooltip delay={300}>
                <Tooltip.Trigger>
                  <span>Доставлено</span>
                </Tooltip.Trigger>
                <Tooltip.Content>
                  Приём APNs/FCM (Expo «ok»), не показ на экране
                </Tooltip.Content>
              </Tooltip>
            </div>
            <div className="pcd-metric__value">{delivered}</div>
          </div>
          <div className="pcd-metric">
            <div className="pcd-metric__label">Не доставлено</div>
            <div className="pcd-metric__value">
              {c?.failedDeliveryCount ?? "—"}
            </div>
          </div>
          <div className="pcd-metric">
            <div className="pcd-metric__label">Открыли</div>
            <div className="pcd-metric__value">{opened}</div>
          </div>
          <div className="pcd-metric">
            <div className="pcd-metric__label">Записались без открытия</div>
            <div className="pcd-metric__value">
              {c?.bookedWithoutOpenCount ?? "—"}
            </div>
          </div>
          <div className="pcd-metric">
            <div className="pcd-metric__label">Выручка</div>
            <div className="pcd-metric__value">{formatRevenue(c?.revenueRub)}</div>
          </div>
        </div>

        <section className="pcd-panel">
          <div className="pcd-panel__head">
            <h2 className="pcd-panel__title">Воронка</h2>
            <span className="pcd-panel__hint">{funnelHint}</span>
          </div>
          <div className="pcd-funnel">
            {FUNNEL_STAGES.map((stage, index) => {
              const count = funnelCounts[index];
              const fromPrev =
                index === 0 ? delivered : funnelCounts[index - 1];
              const barPct =
                delivered > 0
                  ? Math.min(100, Math.round((count / delivered) * 100))
                  : 0;
              return (
                <div key={stage} className="pcd-funnel__row">
                  <span className="pcd-funnel__label">
                    {FUNNEL_STAGE_LABELS[stage]}
                  </span>
                  <div className="pcd-funnel__bar" aria-hidden>
                    <div
                      className="pcd-funnel__bar-fill"
                      style={{ width: `${barPct}%` }}
                    />
                  </div>
                  <span>{count}</span>
                  <span className="pcd-funnel__stat">
                    от пред.: {pct(count, fromPrev)}
                  </span>
                  <span className="pcd-funnel__stat">
                    от дост.: {pct(count, delivered)}
                  </span>
                </div>
              );
            })}
          </div>
        </section>

        <section className="pcd-panel">
          <div className="pcd-panel__head">
            <h2 className="pcd-panel__title">Получатели</h2>
            <Button
              variant="outline"
              isPending={store.isExportingCsv}
              onPress={() => void store.exportRecipientsCsv(uuid)}
            >
              <Icon path={mdiDownload} size={0.7} />
              Выгрузить CSV
            </Button>
          </div>

          <div className="pcd-filters">
            <SearchField
              className="min-w-[200px]"
              value={store.recipientsSearch}
              onChange={(v) => store.setRecipientsFilters({ search: v })}
              aria-label="Поиск получателей"
            >
              <SearchField.Group>
                <SearchField.SearchIcon />
                <SearchField.Input placeholder="Имя или телефон" />
                <SearchField.ClearButton />
              </SearchField.Group>
            </SearchField>
            <Select
              className="min-w-[160px]"
              selectedKey={store.recipientsStatus || "all"}
              onSelectionChange={(key) =>
                store.setRecipientsFilters({ status: String(key ?? "all") })
              }
              aria-label="Статус получателя"
            >
              <Select.Trigger>
                <Select.Value />
                <Select.Indicator />
              </Select.Trigger>
              <Select.Popover>
                <ListBox>
                  <ListBox.Item id="all" textValue="Все статусы">
                    Все статусы
                  </ListBox.Item>
                  {Object.entries(RECIPIENT_STATUS_LABELS).map(
                    ([value, label]) => (
                      <ListBox.Item key={value} id={value} textValue={label}>
                        {label}
                      </ListBox.Item>
                    ),
                  )}
                </ListBox>
              </Select.Popover>
            </Select>
            <Select
              className="min-w-[160px]"
              selectedKey={store.recipientsOpened || "all"}
              onSelectionChange={(key) =>
                store.setRecipientsFilters({ opened: String(key ?? "all") })
              }
              aria-label="Открытие"
            >
              <Select.Trigger>
                <Select.Value />
                <Select.Indicator />
              </Select.Trigger>
              <Select.Popover>
                <ListBox>
                  <ListBox.Item id="all" textValue="Открытие: все">
                    Открытие: все
                  </ListBox.Item>
                  <ListBox.Item id="true" textValue="Открыли">
                    Открыли
                  </ListBox.Item>
                  <ListBox.Item id="false" textValue="Не открыли">
                    Не открыли
                  </ListBox.Item>
                </ListBox>
              </Select.Popover>
            </Select>
            <Select
              className="min-w-[180px]"
              selectedKey={store.recipientsFunnelStage || "all"}
              onSelectionChange={(key) =>
                store.setRecipientsFilters({
                  funnelStage: String(key ?? "all"),
                })
              }
              aria-label="Стадия"
            >
              <Select.Trigger>
                <Select.Value />
                <Select.Indicator />
              </Select.Trigger>
              <Select.Popover>
                <ListBox>
                  <ListBox.Item id="all" textValue="Все стадии">
                    Все стадии
                  </ListBox.Item>
                  {FUNNEL_STAGES.map((stage) => (
                    <ListBox.Item
                      key={stage}
                      id={stage}
                      textValue={FUNNEL_STAGE_LABELS[stage]}
                    >
                      {FUNNEL_STAGE_LABELS[stage]}
                    </ListBox.Item>
                  ))}
                </ListBox>
              </Select.Popover>
            </Select>
          </div>

          {store.isRecipientsLoading && store.recipients.length === 0 ? (
            <div className="pcd-loading">
              <Spinner size="lg" />
            </div>
          ) : (
            <Table>
              <Table.ScrollContainer>
                <Table.Content aria-label="Получатели рассылки">
                  <Table.Header>
                    <Table.Column id="client" isRowHeader>
                      Клиент
                    </Table.Column>
                    <Table.Column id="delivery">Доставка</Table.Column>
                    <Table.Column id="opened">Открыл</Table.Column>
                    <Table.Column id="stage">Стадия</Table.Column>
                    <Table.Column id="booking">Заявка</Table.Column>
                  </Table.Header>
                  <Table.Body>
                    {store.recipients.length === 0 ? (
                      <Table.Row>
                        <Table.Cell colSpan={5}>
                          <div className="pcd-table-empty">
                            Получателей ещё нет
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    ) : (
                      store.recipients.map((r) => (
                        <Table.Row key={r.uuid ?? r.clientUuid}>
                          <Table.Cell>
                            <div className="pcd-client-cell">
                              <Link
                                className="pcd-client-cell__name"
                                to={ROUTES.CLIENT_DETAIL.replace(
                                  ":uuid",
                                  r.clientUuid,
                                )}
                              >
                                {r.clientName ?? "—"}
                              </Link>
                              {r.clientPhone && (
                                <span className="pcd-client-cell__phone">
                                  {r.clientPhone}
                                </span>
                              )}
                            </div>
                          </Table.Cell>
                          <Table.Cell>
                            {RECIPIENT_STATUS_LABELS[r.status] ?? r.status}
                            {r.skipReason && (
                              <span className="pcd-delivery-sub">
                                {SKIP_REASON_LABELS[r.skipReason] ??
                                  r.skipReason}
                              </span>
                            )}
                            {r.deviceErrorCodes &&
                              r.deviceErrorCodes.length > 0 && (
                                <span className="pcd-delivery-sub">
                                  {r.deviceErrorCodes.join(", ")}
                                </span>
                              )}
                            {r.isTest && (
                              <span className="pcd-delivery-sub">Тест</span>
                            )}
                          </Table.Cell>
                          <Table.Cell>
                            {r.opened === true
                              ? "Да"
                              : r.opened === false
                                ? "Нет"
                                : "—"}
                          </Table.Cell>
                          <Table.Cell>
                            {r.funnelStage
                              ? FUNNEL_STAGE_LABELS[
                                  r.funnelStage as keyof typeof FUNNEL_STAGE_LABELS
                                ] ?? r.funnelStage
                              : "—"}
                          </Table.Cell>
                          <Table.Cell>
                            {r.bookingUuid ? (
                              <span className="text-sm">
                                {r.bookingStatus ?? r.bookingUuid}
                                {r.bookingTotalCost != null
                                  ? ` · ${r.bookingTotalCost} ₽`
                                  : ""}
                              </span>
                            ) : (
                              "—"
                            )}
                          </Table.Cell>
                        </Table.Row>
                      ))
                    )}
                  </Table.Body>
                </Table.Content>
              </Table.ScrollContainer>
            </Table>
          )}

          <div className="pcd-recipients-footer">
            <span>
              {recipientsShown} из {recipientsTotal}
            </span>
            <div className="pcd-recipients-footer__actions">
              <Button
                variant="outline"
                size="sm"
                isDisabled={!store.recipientsMeta?.hasPrevPage}
                onPress={() =>
                  store.setRecipientsPage(store.recipientsPage - 1)
                }
              >
                Назад
              </Button>
              <Button
                variant="outline"
                size="sm"
                isDisabled={!store.recipientsMeta?.hasNextPage}
                onPress={() =>
                  store.setRecipientsPage(store.recipientsPage + 1)
                }
              >
                Дальше
              </Button>
            </div>
          </div>
        </section>
      </div>

      <Modal.Backdrop isOpen={cancelOpen} onOpenChange={setCancelOpen}>
        <Modal.Container>
          <Modal.Dialog>
            <Modal.Header>
              <Modal.Heading>Отменить рассылку?</Modal.Heading>
            </Modal.Header>
            <Modal.Body>Рассылка будет отменена и не отправится.</Modal.Body>
            <Modal.Footer>
              <Button variant="ghost" onPress={() => setCancelOpen(false)}>
                Назад
              </Button>
              <Button
                variant="danger"
                onPress={async () => {
                  await store.cancelCampaign(uuid);
                  setCancelOpen(false);
                }}
              >
                Отменить
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </>
  );
});

PushCampaignDetailPage.displayName = "PushCampaignDetailPage";
export default PushCampaignDetailPage;
